// ---- DNI ----
/** Enmascara el DNI para mostrar en UI: 12345678A → ****5678A */
export function maskDni(dni: string): string {
  if (dni.length < 5) return '****';
  return `****${dni.slice(-5)}`;
}

// ---- Fechas ----
export function formatDate(dateStr: string, locale = 'es-ES'): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateStr));
}

export function formatMonthYear(month: number, year: number, locale = 'es-ES'): string {
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
}

// ---- Importes ----
/** Convierte céntimos a string formateado: 9500 → "95,00 €" */
export function formatAmount(cents: number, locale = 'es-ES'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// ---- Strings ----
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
}

// ---- Encoding ----
/**
 * Repairs latin1-decoded strings that were actually UTF-8 bytes ("mojibake").
 *
 * Multer/busboy decode multipart FILENAMES as latin1 even when the browser
 * sent UTF-8 (the RFC 7578 default), so "á" (bytes C3 A1) arrives as "Ã¡".
 * Reinterprets the code units as bytes and decodes them as UTF-8, with a
 * triple guard so valid input is never corrupted (ADR-024):
 *   1. pure ASCII → returned as-is (identical in both encodings);
 *   2. any code point above U+00FF → real Unicode text, not a latin1
 *      mis-decode (a latin1 decoder can only ever produce U+0000–U+00FF);
 *   3. reinterpretation is not valid UTF-8 → genuine latin1 text (e.g. a
 *      lone "é" byte), returned as-is.
 */
export function fixLatin1Mojibake(value: string): string {
  if (!/[\u0080-\u00ff]/.test(value)) return value; // pure ASCII
  const bytes = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code > 0xff) return value; // real Unicode text
    bytes[i] = code;
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return value; // genuine latin1 — keep as-is
  }
}

// ---- Storage ----
/**
 * Returns a string safe to use as a Supabase Storage object key.
 * Supabase only accepts ASCII word chars + a small set of punctuation, so
 * accents/eñe/curly quotes/etc. are rejected. This helper transliterates
 * known typography to ASCII, strips diacritics, and replaces any leftover
 * disallowed char with `_`. Operates on a single basename — do NOT pass paths.
 */
const STORAGE_PRE_MAP: Record<string, string> = {
  '´': "'",
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
  '…': '...',
  '°': 'o',
  'º': 'o',
  'ª': 'a',
  '¿': '?',
  '¡': '!',
};
const STORAGE_ALLOWED_EXTRA = "_-!.*'() &$@=;:+,?";

// ---- Turnos ----

function normalizeShiftName(s?: string | null): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Única fuente de verdad de «¿es un turno a distancia?». La modalidad va
 * codificada en el nombre del turno (no hay campo explícito en la tabla
 * shifts), así que panel y móvil hacen el match aquí.
 */
export function isDistanceShift(shiftName?: string | null): boolean {
  return normalizeShiftName(shiftName).includes('distancia');
}

/**
 * Nivel del turno derivado del nombre («Intensivo Mañana - Inicio» → 'inicio').
 * Los turnos presenciales de Policía Local llevan el sufijo «- Inicio» /
 * «- Avanzado»; «A Distancia» y los turnos de otras oposiciones no tienen
 * nivel → null. No hay columna level en la tabla shifts: si se renombran los
 * turnos, este match deja de encontrar niveles (ver DECISION_LOG).
 */
export function shiftLevel(shiftName?: string | null): 'inicio' | 'avanzado' | null {
  const n = normalizeShiftName(shiftName);
  if (n.includes('inicio')) return 'inicio';
  if (n.includes('avanzado')) return 'avanzado';
  return null;
}

/** Etiqueta corta del turno para mostrar junto al nombre del alumno. */
export function shiftBadgeLabel(
  shiftName?: string | null,
): 'Inicio' | 'Avanzado' | 'A Distancia' | null {
  const level = shiftLevel(shiftName);
  if (level === 'inicio') return 'Inicio';
  if (level === 'avanzado') return 'Avanzado';
  if (isDistanceShift(shiftName)) return 'A Distancia';
  return null;
}

export function sanitizeStorageKey(name: string): string {
  const pre = name
    .split('')
    .map((c) => STORAGE_PRE_MAP[c] ?? c)
    .join('');
  const stripped = pre.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const out = stripped
    .split('')
    .map((c) => {
      if (/[A-Za-z0-9]/.test(c)) return c;
      if (STORAGE_ALLOWED_EXTRA.includes(c)) return c;
      return '_';
    })
    .join('')
    .trim();
  return out || '_';
}
